import { Badge, Box, Center, Flex, Image, Spinner, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { FiMapPin, FiClock } from 'react-icons/fi';
import EventsService from '../../../../services/events/eventsService';
import EventParticipantsCardList from '../../components/EventParticipantsCardList/EventParticipantsCardList';
import { useAppToast } from '../../../../hooks/useAppToast';
import { BASE_URL } from '../../../../config';

const EventParticipantsPage = () => {
    const { id } = useParams();
    const { t } = useTranslation('default');
    const navigate = useNavigate();
    const toast = useAppToast();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchEvent = async () => {
            try {
                const res = await EventsService.getEvent(id);
                setEvent(res.data.data);
            } catch {
                toast({ title: t('request.error'), status: 'error', duration: 3000, isClosable: true });
            } finally {
                setLoading(false);
            }
        };
        fetchEvent();
    }, [id]);

    if (loading) {
        return (
            <Center w="100%" py="80px">
                <Spinner size="xl" color="brand.500" />
            </Center>
        );
    }

    const coverImgSrc = event?.coverImage ? `${BASE_URL}/${event.coverImage}` : null;

    const formatDateRange = () => {
        if (!event) return '';
        const start = `${moment(event.startDate).format('D.MM.YYYY')} ${event.startTime}`;
        if (event.startDate === event.endDate) {
            return `${start} — ${event.endTime}`;
        }
        return `${start} — ${moment(event.endDate).format('D.MM.YYYY')} ${event.endTime}`;
    };

    return (
        <Flex w="100%" maxW="7xl" flexDirection="column" px={{ base: 4, md: 4 }} gap="32px" py={{ base: '40px', md: '80px' }}>

            {/* Nav */}
            <Flex
                w="100%"
                maxW="7xl"
                className="nav-wrapper"
                gap="16px"
                align="center"
            >
                <Flex gap="16px" align="center">
                    <Flex onClick={() => navigate('/')} cursor="pointer" align="center" flexShrink={0}>
                        <svg width="20" height="17" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 15.5L1 8.5M1 8.5L8 1.5M1 8.5H19" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Flex>
                    <Text fontFamily="Poppins" fontWeight="500" fontSize="22px" lineHeight="1.3" color="#363636">
                        {t('event.participants.title')}
                    </Text>
                </Flex>
            </Flex>

            {/* Event banner */}
            {event && (
                <Box position="relative" w="100%" borderRadius="8px" overflow="hidden" minH={{ base: '240px', md: '200px' }}>
                    {coverImgSrc && (
                        <Image
                            src={coverImgSrc}
                            alt={event.title}
                            position="absolute"
                            top="0"
                            left="0"
                            w="100%"
                            h="100%"
                            objectFit="cover"
                        />
                    )}
                    <Box
                        position="absolute"
                        top="0" left="0" right="0" bottom="0"
                        background="rgba(8,154,122,0.90)"
                    />
                    <Flex
                        position="relative"
                        p={{ base: '24px', md: '40px' }}
                        direction="column"
                        gap="12px"
                        maxW={{ base: '100%', md: '62%' }}
                        minH={{ base: '240px', md: '200px' }}
                        justify="center"
                    >
                        {event.categories?.length > 0 && (
                            <Flex gap="8px" flexWrap="wrap">
                                {event.categories.map((cat, i) => (
                                    <Badge
                                        key={i}
                                        bg="rgba(255,255,255,0.18)"
                                        color="white"
                                        fontFamily="Poppins"
                                        fontWeight="500"
                                        fontSize="11px"
                                        px={3}
                                        py={1}
                                        borderRadius="full"
                                        textTransform="none"
                                        letterSpacing="0.5px"
                                    >
                                        {cat}
                                    </Badge>
                                ))}
                            </Flex>
                        )}
                        <Text
                            color="white"
                            fontFamily="Poppins"
                            fontWeight="700"
                            fontSize={{ base: '17px', md: '21px' }}
                            lineHeight="1.35"
                        >
                            {event.title}
                        </Text>
                        {event.address && (
                            <Flex align="flex-start" gap="8px">
                                <Box as={FiMapPin} color="white" boxSize="14px" mt="2px" flexShrink={0} />
                                <Text color="rgba(255,255,255,0.88)" fontFamily="Nunito" fontSize="14px" lineHeight="1.4">
                                    {event.address}
                                </Text>
                            </Flex>
                        )}
                        <Flex align="center" gap="8px">
                            <Box as={FiClock} color="white" boxSize="14px" flexShrink={0} />
                            <Text color="rgba(255,255,255,0.88)" fontFamily="Nunito" fontSize="14px">
                                {formatDateRange()}
                            </Text>
                        </Flex>
                    </Flex>
                </Box>
            )}

            {/* Participants table */}
            {event && (
                <EventParticipantsCardList
                    eventId={event.id}
                    isEditing={true}
                    eventName={event.title}
                    eventData={event}
                />
            )}
        </Flex>
    );
};

export default EventParticipantsPage;
